/** Web adaptation of Apple WWDC25/219: a quiet selection lifts on touch.
 * Existing buttons own navigation, permissions and beforeLeave handling.
 */
export function attachGlassRail(rail, {buttonSelector = ":scope > button:not(:disabled)"} = {}) {
  const lens = document.createElement('span');
  lens.className = 'continuous-primary-lens';
  lens.setAttribute('aria-hidden', 'true');
  rail.prepend(lens);
  rail.dataset.glassRail = 'true';
  let gesture = null;
  let hover = null;
  const copies = new Map();
  const clearOptics = () => {
    copies.forEach((copy, svg) => {
      svg.style.removeProperty("mask-image");
      svg.style.removeProperty("-webkit-mask-image");
      copy.remove();
    });
    copies.clear();
  };
  let frame = 0;
  let suppressUntil = 0;
  const buttons = () => [...rail.querySelectorAll(buttonSelector)];
  const current = () => buttons().find(button => button.hasAttribute('aria-current'));
  const horizontal = () => rail.getBoundingClientRect().width > rail.getBoundingClientRect().height;
  const nearest = (x, y) => buttons().reduce((best, button) => {
    const r = button.getBoundingClientRect();
    const distance = horizontal() ? Math.abs(x-r.left-r.width/2) : Math.abs(y-r.top-r.height/2);
    return !best || distance < best.distance ? {button, distance} : best;
  }, null)?.button;
  const paint = () => {
    frame = 0;
    // Taps reflect the accepted route, not a speculative press destination.
    // Otherwise release repaints the old route before navigation commits.
    const button = gesture?.dragged ? gesture.preview : hover ? nearest(hover.x, hover.y) : current();
    lens.hidden = !button;
    if (!button) { clearOptics(); return; }
    const r = rail.getBoundingClientRect(), b = button.getBoundingClientRect();
    const wide = horizontal();
    let x = b.left-r.left+rail.scrollLeft, y = b.top-r.top+rail.scrollTop;
    const iconOnly = rail.classList.contains('continuous-vessel-primary--icon-only');
    const lower = rail.classList.contains('continuous-vessel-primary--segmented');
    // Match the control, never its glyph or the moving material's bounds.
    // One square footprint is retained through press, drag and release.
    const size = Math.min(44, b.width, b.height);
    const width = iconOnly ? size : b.width-(lower?10:0);
    const height = iconOnly ? size : b.height;
    y += (b.height-height)/2;
    x += (b.width-width)/2;
    const tracking = gesture?.dragged ? gesture : hover;
    if (tracking) {
      // Direct tracking without stretching the selection footprint.
      if (wide) {
        x = Math.max(Math.min(...buttons().map(item=>item.getBoundingClientRect().left-r.left+rail.scrollLeft))-3, Math.min(Math.max(...buttons().map(item=>item.getBoundingClientRect().right-r.left+rail.scrollLeft))-width+3, tracking.x-r.left+rail.scrollLeft-width/2));
      } else {
        y = Math.max(Math.min(...buttons().map(item=>item.getBoundingClientRect().top-r.top+rail.scrollTop))-3, Math.min(Math.max(...buttons().map(item=>item.getBoundingClientRect().bottom-r.top+rail.scrollTop))-height+3, tracking.y-r.top+rail.scrollTop-height/2));
      }
    }
    lens.style.width = `${width}px`;
    lens.style.height = `${height}px`;
    lens.style.transform = `translate3d(${x}px,${y}px,0)`;
    // Draw larger vector geometry only inside the clear circular lens. Mask
    // the original in that exact area so partial overlap never doubles a glyph.
    const optical = iconOnly && Boolean(hover || gesture);
    lens.toggleAttribute('data-magnifying', optical);
    if (!optical) clearOptics();
    else buttons().forEach(item => {
      const svg = item.querySelector('.continuous-primary-cap > svg');
      if (!svg) return;
      const g = svg.getBoundingClientRect();
      if (!g.width || !g.height) return;
      let copy = copies.get(svg);
      if (!copy) {
        copy = svg.cloneNode(true);
        copy.classList.add('continuous-primary-lens-copy');
        copy.setAttribute('aria-hidden', 'true');
        copy.setAttribute('focusable', 'false');
        copy.style.cssText = '';
        lens.append(copy);
        copies.set(svg, copy);
      }
      const magnifiedWidth = g.width * 1.3, magnifiedHeight = g.height * 1.3;
      copy.style.width = `${magnifiedWidth}px`;
      copy.style.height = `${magnifiedHeight}px`;
      copy.style.left = `${g.left-r.left+rail.scrollLeft+g.width/2-x-magnifiedWidth/2-1}px`;
      copy.style.top = `${g.top-r.top+rail.scrollTop+g.height/2-y-magnifiedHeight/2-1}px`;
      const cx = r.left+x-rail.scrollLeft+width/2-g.left;
      const cy = r.top+y-rail.scrollTop+height/2-g.top;
      const radius = width/2-1;
      const mask = `radial-gradient(circle ${radius}px at ${cx}px ${cy}px, transparent ${radius}px, black ${radius+0.25}px)`;
      svg.style.setProperty('mask-image', mask);
      svg.style.setProperty('-webkit-mask-image', mask);
    });
    buttons().forEach(item => {
      const rect = item.getBoundingClientRect();
      const center = wide ? rect.left+rect.width/2 : rect.top+rect.height/2;
      const focus = gesture ? Math.max(0,1-Math.abs((wide?gesture.x:gesture.y)-center)/(wide?rect.width:rect.height)) : item===button ? 1 : 0;
      item.style.setProperty('--glass-focus', focus.toFixed(3));
    });
  };
  const schedule = () => {if (!frame) frame = requestAnimationFrame(paint);};
  const release = () => {
    const id = gesture?.id;
    gesture = null;
    delete rail.dataset.glassPressed;
    delete rail.dataset.glassDragging;
    if (id != null && rail.hasPointerCapture?.(id)) rail.releasePointerCapture(id);
    schedule();
  };
  const down = event => {
    if (gesture || event.button!==0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    const button = event.target.closest('button');
    if (!button || button.parentElement!==rail || !buttons().includes(button) || button.disabled) return;
    gesture = {id:event.pointerId,startX:event.clientX,startY:event.clientY,x:event.clientX,y:event.clientY,delta:0,preview:button,dragged:false};
    rail.dataset.glassPressed = 'true';
    rail.setPointerCapture?.(event.pointerId);
    schedule();
  };
  const move = event => {
    if (!gesture) {
      if (event.pointerType === 'touch') return;
      const target = event.target.closest('button');
      // Independent Profile and Meet TOTO controls never attract this lens.
      if (target && !buttons().includes(target)) { hover = null; schedule(); return; }
      const r = rail.getBoundingClientRect();
      const bounds = buttons().map(item => item.getBoundingClientRect());
      const inRange = horizontal()
        ? event.clientX >= Math.min(...bounds.map(b=>b.left)) && event.clientX <= Math.max(...bounds.map(b=>b.left+b.width))
        : event.clientY >= Math.min(...bounds.map(b=>b.top)) && event.clientY <= Math.max(...bounds.map(b=>b.top+b.height));
      hover = inRange && event.clientX >= r.left && event.clientX <= r.left+r.width && event.clientY >= r.top && event.clientY <= r.top+r.height
        ? {x:event.clientX, y:event.clientY} : null;
      schedule();
      return;
    }
    if (gesture.id!==event.pointerId) return;
    const previous = horizontal()?gesture.x:gesture.y;
    gesture.x=event.clientX;gesture.y=event.clientY;
    gesture.delta=(horizontal()?gesture.x:gesture.y)-previous;
    gesture.dragged ||= Math.hypot(gesture.x-gesture.startX,gesture.y-gesture.startY)>6;
    if (gesture.dragged) {
      event.preventDefault();
      rail.dataset.glassDragging='true';
      gesture.preview=nearest(gesture.x,gesture.y);
      if (event.pointerType !== 'touch') hover = {x:gesture.x,y:gesture.y};
    }
    schedule();
  };
  const up = event => {
    if (gesture?.id!==event.pointerId) return;
    const dragged=gesture.dragged;
    // Read the release coordinates, not the previous animation frame.
    const destination=nearest(event.clientX,event.clientY);
    const tapped=gesture.preview;
    suppressUntil=Date.now()+400;
    release();
    // Pointer capture retargets the browser click to the rail. Dispatch once
    // through the original button, including its unsaved-work guard.
    (dragged?destination:tapped)?.click();
  };
  const cancel = event => {if(gesture?.id===event.pointerId){hover=null;suppressUntil=Date.now()+400;release();}};
  const click = event => {
    const target = event.target.closest('button');
    if (target && !buttons().includes(target)) return;
    if(event.isTrusted && event.detail!==0 && Date.now()<suppressUntil){event.preventDefault();event.stopImmediatePropagation();}
  };
  const leave = () => { hover = null; schedule(); };
  const interrupt = () => { hover = null; if (gesture) suppressUntil=Date.now()+400; release(); };
  const resize = typeof ResizeObserver==='undefined'?null:new ResizeObserver(interrupt);
  resize?.observe(rail);
  const mutations = new MutationObserver(()=>{if(gesture) interrupt();else schedule();});
  mutations.observe(rail,{childList:true});
  buttons().forEach(button=>mutations.observe(button,{attributes:true,attributeFilter:['aria-current','disabled']}));
  rail.addEventListener('pointerdown',down);
  rail.addEventListener('pointermove',move);
  rail.addEventListener('pointerleave',leave);
  rail.addEventListener('pointerup',up);
  rail.addEventListener('pointercancel',cancel);
  rail.addEventListener('lostpointercapture',cancel);
  rail.addEventListener('click',click,true);
  rail.addEventListener('scroll',schedule);
  window.addEventListener('blur',interrupt);
  paint();
  return () => {
    hover=null;release();clearOptics();cancelAnimationFrame(frame);resize?.disconnect();mutations.disconnect();lens.remove();
    [['pointerdown',down],['pointermove',move],['pointerleave',leave],['pointerup',up],['pointercancel',cancel],['lostpointercapture',cancel],['scroll',schedule]].forEach(([type,handler])=>rail.removeEventListener(type,handler));
    rail.removeEventListener('click',click,true);window.removeEventListener('blur',interrupt);
    delete rail.dataset.glassRail;
    buttons().forEach(button=>button.style.removeProperty('--glass-focus'));
  };
}
